<template>
  <Teleport to="body">
    <transition-group
      name="notification-list"
      tag="div"
      class="notification-container"
      :class="{ 'notification-container--top': position === 'top', 'notification-container--bottom': position === 'bottom' }"
    >
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          'notification',
          `notification--${notification.type}`,
          { 'notification--closing': notification.closing }
        ]"
        :role="notification.type === 'error' ? 'alert' : 'status'"
        :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
        :aria-label="getAriaLabel(notification)"
        tabindex="0"
        @keydown.enter="close(notification.id)"
        @keydown.space.prevent="close(notification.id)"
      >
        <div class="notification__content">
          <div class="notification__icon">
            <component :is="getIcon(notification.type)" />
          </div>
          <div class="notification__message">
            {{ notification.message }}
          </div>
          <button
            class="notification__close"
            @click="close(notification.id)"
            :aria-label="closeLabel"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div
          v-if="notification.duration > 0"
          class="notification__progress"
          :style="{ animationDuration: `${notification.duration}ms` }"
        ></div>
      </div>
    </transition-group>
  </Teleport>
</template>

<script>
import { ref, onMounted } from 'vue'

// Iconos SVG como componentes
const CheckIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  `
}

const XIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  `
}

const AlertTriangleIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  `
}

const InfoIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 16v-4"></path>
      <path d="M12 8h.01"></path>
    </svg>
  `
}

export default {
  name: 'NotificationSystem',
  props: {
    position: {
      type: String,
      default: 'top-right',
      validator: value => ['top-right', 'top-left', 'bottom-right', 'bottom-left', 'top', 'bottom'].includes(value)
    }
  },
  setup() {
    const notifications = ref([])
    let notificationId = 0

    const add = (message, type = 'info', options = {}) => {
      const id = ++notificationId
      const notification = {
        id,
        message,
        type,
        duration: options.duration || 5000,
        closing: false,
        timestamp: Date.now(),
        ...options
      }

      notifications.value.push(notification)

      console.log('Notificación agregada:', { id, message, type, total: notifications.value.length })

      // Auto-close después de la duración
      if (notification.duration > 0) {
        setTimeout(() => {
          close(id)
        }, notification.duration)
      }

      // Log para auditoría
      console.log(`[NOTIFICATION] ${type.toUpperCase()}: ${message}`)

      return id
    }

    const close = (id) => {
      const notification = notifications.value.find(n => n.id === id)
      if (notification) {
        notification.closing = true
        setTimeout(() => {
          const index = notifications.value.findIndex(n => n.id === id)
          if (index > -1) {
            notifications.value.splice(index, 1)
          }
        }, 300) // Tiempo de animación
      }
    }

    const clear = () => {
      notifications.value.forEach(notification => {
        close(notification.id)
      })
    }

    // Métodos públicos
    const success = (message, options) => add(message, 'success', options)
    const error = (message, options) => add(message, 'error', options)
    const warning = (message, options) => add(message, 'warning', options)
    const info = (message, options) => add(message, 'info', options)

    // Exponer métodos globalmente
    if (typeof window !== 'undefined') {
      window.$notification = { success, error, warning, info, close, clear }
    }

    onMounted(() => {
      // Ya definido
    })

    return {
      notifications,
      success,
      error,
      warning,
      info,
      close,
      clear
    }
  },
  data() {
    return {
      closeLabel: 'Cerrar notificación'
    }
  },
  methods: {
    getIcon(type) {
      const icons = {
        success: CheckIcon,
        error: XIcon,
        warning: AlertTriangleIcon,
        info: InfoIcon
      }
      return icons[type] || InfoIcon
    },
    getAriaLabel(notification) {
      const labels = {
        success: 'Notificación de éxito',
        error: 'Notificación de error',
        warning: 'Notificación de advertencia',
        info: 'Notificación informativa'
      }
      return `${labels[notification.type]}: ${notification.message}`
    }
  }
}
</script>

<style scoped>
.notification-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  max-width: 400px;
}

.notification-container--top {
  top: 20px;
}

.notification-container--bottom {
  bottom: 20px;
}

.notification-container--top .notification-container {
  right: 20px;
}

.notification-container--bottom .notification-container {
  right: 20px;
}

.notification {
  margin-bottom: 10px;
  pointer-events: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

.notification--success {
  background-color: #28a745;
  color: white;
}

.notification--error {
  background-color: #dc3545;
  color: white;
}

.notification--warning {
  background-color: #ffc107;
  color: #212529;
}

.notification--info {
  background-color: #17a2b8;
  color: white;
}

.notification--closing {
  animation: slideOut 0.3s ease-in forwards;
}

.notification__content {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
}

.notification__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification__message {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
}

.notification__close {
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.8;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.notification__close:hover {
  opacity: 1;
}

.notification__progress {
  height: 3px;
  background-color: rgba(255, 255, 255, 0.3);
  animation: progress linear;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Animaciones de lista */
.notification-list-enter-active,
.notification-list-leave-active {
  transition: all 0.3s ease;
}

.notification-list-enter-from,
.notification-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-list-move {
  transition: transform 0.3s ease;
}

/* Responsive */
@media (max-width: 480px) {
  .notification-container {
    left: 10px;
    right: 10px;
    max-width: none;
  }

  .notification__content {
    padding: 12px;
    gap: 8px;
  }

  .notification__message {
    font-size: 13px;
  }
}

/* Soporte para navegación por teclado */
.notification:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Soporte para lectores de pantalla */
@media (prefers-reduced-motion: reduce) {
  .notification,
  .notification--closing {
    animation: none;
  }

  .notification__progress {
    display: none;
  }
}
</style>